// Files in a Spec chat: shared by the web, the API and the Spec Worker.

export const SPEC_FILE_KINDS = ['image', 'pdf', 'docx', 'xlsx', 'pptx', 'csv', 'text'] as const;
export type SpecFileKind = (typeof SPEC_FILE_KINDS)[number];
export type SpecFileSource = 'upload' | 'generated';
export type SpecFileStatus = 'pending' | 'ready' | 'failed';

export const SPEC_FILE_LIMITS = {
  maxBytes: 20 * 1024 * 1024,
  imageMaxBytes: 5 * 1024 * 1024,
  /** Tokens follow pixels (measured 2026-10-06: 1568x1176 is 2,192 tokens; 4000x3000 is 14,117). */
  imageMaxEdge: 1_568,
  perMessage: 5,
  textMaxChars: 50_000,
  pageChars: 12_000,
  readsPerTurn: 3,
  /** Reservation allowance per image: a 1568x1568 image measured 2,898 tokens. */
  imageTokens: 3_000,
  nameMaxChars: 255,
} as const;

/** Kinds whose text becomes an empty Knowledge page's body: the API imports them, the web waits for them. */
export const KNOWLEDGE_IMPORT_KINDS: readonly SpecFileKind[] = ['docx', 'text'];
/** Knowledge limits the API enforces and the web checks first; a batch is ten files (Office files unzip in memory). */
export const KNOWLEDGE_LIMITS = {
  titleMaxChars: 200,
  /** Folders nest at most this deep. */
  folderDepthMax: 5,
  /** A folder name, one path segment. */
  folderNameMaxChars: 100,
  descriptionMaxChars: 300,
  /** A description as typed: room for the spaces the stored line collapses. */
  descriptionInputMaxChars: 600,
  uploadBatchFiles: 10,
  /** How far one Spec answer reads into a page or file before it says how far it got. */
  readOnMaxChars: 60_000,
} as const;
/** The refusal a file over the size limit gets, from the API and the web alike. */
export const KNOWLEDGE_FILE_TOO_LARGE = `Files must be ${SPEC_FILE_LIMITS.maxBytes / 1024 / 1024} MB or smaller.`;

export const SPEC_FILE_ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'text/markdown',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.pdf',
  '.docx',
  '.xlsx',
  '.pptx',
  '.csv',
  '.txt',
  '.md',
].join(',');

/** A file on a member's message; `pages` is set for documents and absent for images. */
export interface SpecFilePart {
  type: 'file';
  fileId: string;
  name: string;
  mimeType: string;
  size: number;
  kind: SpecFileKind;
  pages?: number;
}

export type SpecFileSegment =
  | { kind: 'text'; text: string }
  | { kind: 'file'; name: string; id: string };

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const UUID_RE = new RegExp(`^${UUID}$`);
const DIRECTIVE_SOURCE = `:file\\[([^\\]\\n]{1,${SPEC_FILE_LIMITS.nameMaxChars}})\\]\\{id=(${UUID})\\}`;

export function specFilePages(extractedChars: number): number {
  return Math.max(1, Math.ceil(extractedChars / SPEC_FILE_LIMITS.pageChars));
}

export function sanitizeSpecFileName(name: string): string {
  const cleaned = name
    .replace(/[[\]{}\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, SPEC_FILE_LIMITS.nameMaxChars)
    .trim();
  return cleaned.length > 0 ? cleaned : 'file';
}

export function specFileDirective(name: string, id: string): string {
  return `:file[${sanitizeSpecFileName(name)}]{id=${id}}`;
}

export function parseSpecFileDirectives(text: string): SpecFileSegment[] {
  const segments: SpecFileSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(new RegExp(DIRECTIVE_SOURCE, 'g'))) {
    const index = match.index ?? 0;
    if (index > last) segments.push({ kind: 'text', text: text.slice(last, index) });
    segments.push({ kind: 'file', name: match[1] ?? '', id: match[2] ?? '' });
    last = index + match[0].length;
  }
  if (last < text.length) segments.push({ kind: 'text', text: text.slice(last) });
  return segments;
}

export function specFileDirectivesToPlain(text: string): string {
  return text.replace(new RegExp(DIRECTIVE_SOURCE, 'g'), (_match, name: string) => `[${name}]`);
}

const PART_KEYS = new Set(['type', 'fileId', 'name', 'mimeType', 'size', 'kind', 'pages']);

export function readSpecFilePart(value: unknown): SpecFilePart | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (Object.keys(record).some((key) => !PART_KEYS.has(key))) return null;
  const { type, fileId, name, mimeType, size, kind, pages } = record;
  if (type !== 'file') return null;
  if (typeof fileId !== 'string' || !UUID_RE.test(fileId)) return null;
  if (typeof name !== 'string' || name.length === 0) return null;
  if (name.length > SPEC_FILE_LIMITS.nameMaxChars) return null;
  if (typeof mimeType !== 'string' || mimeType.length === 0 || mimeType.length > 255) return null;
  if (typeof size !== 'number' || !Number.isSafeInteger(size) || size <= 0) return null;
  if (size > SPEC_FILE_LIMITS.maxBytes) return null;
  if (typeof kind !== 'string' || !(SPEC_FILE_KINDS as readonly string[]).includes(kind)) {
    return null;
  }
  const part: SpecFilePart = { type, fileId, name, mimeType, size, kind: kind as SpecFileKind };
  if (kind === 'image') return pages === undefined ? part : null;
  if (typeof pages !== 'number' || !Number.isSafeInteger(pages) || pages < 1) return null;
  return { ...part, pages };
}
